import {
  collection,
  addDoc,
  updateDoc,
  doc,
  getDocs,
  query,
  where,
  onSnapshot,
  deleteDoc,
  setDoc
} from 'firebase/firestore';
import { 
  createUserWithEmailAndPassword, 
  updateProfile,
  signOut,
  signInWithEmailAndPassword
} from 'firebase/auth';
import { auth, db } from '../firebase';

export const tenantService = {
  // Create a tenant account without affecting current user session
  async createTenantAccount(tenantData) {
    try {
      // Instead of creating the Firebase Auth user immediately (which logs them in),
      // we'll create the account data and let them handle authentication on first login
      
      // Generate a unique tenant ID
      const tenantId = `tenant_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      // Create user document in Firestore (without Firebase Auth user yet)
      await setDoc(doc(db, 'users', tenantId), {
        uid: tenantId,
        email: tenantData.email,
        role: 'tenant',
        displayName: tenantData.name,
        phone: tenantData.phone,
        requiresAccountCreation: true, // Flag to create Firebase Auth account on first login
        defaultPassword: tenantData.defaultPassword, // Store temporarily for first login
        createdAt: new Date()
      });

      // Create tenant-property assignment
      const assignmentRef = await addDoc(collection(db, 'tenantAssignments'), {
        tenantId: tenantId,
        propertyId: tenantData.propertyId,
        ownerId: tenantData.ownerId,
        tenantName: tenantData.name,
        tenantEmail: tenantData.email,
        phone: tenantData.phone,
        leaseStartDate: tenantData.leaseStartDate,
        leaseEndDate: tenantData.leaseEndDate,
        monthlyRent: tenantData.monthlyRent,
        status: 'active',
        createdAt: new Date(),
        updatedAt: new Date()
      });

      // Update property to mark as occupied
      const propertyRef = doc(db, 'properties', tenantData.propertyId);
      await updateDoc(propertyRef, {
        tenantId: tenantId,
        tenantName: tenantData.name,
        tenantEmail: tenantData.email,
        leaseStartDate: tenantData.leaseStartDate,
        leaseEndDate: tenantData.leaseEndDate,
        monthlyRent: tenantData.monthlyRent,
        status: 'occupied',
        updatedAt: new Date()
      });

      // Return the result - the owner stays logged in
      return {
        tenantId: tenantId,
        assignmentId: assignmentRef.id,
        email: tenantData.email,
        defaultPassword: tenantData.defaultPassword
      };
    } catch (error) {
      throw error;
    }
  },

  // Function to create Firebase Auth account when tenant first logs in
  async createFirebaseAuthAccount(tenantId, email, password) {
    try {
      // Create Firebase Auth user
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Update the user document with the real Firebase Auth UID
      await updateDoc(doc(db, 'users', tenantId), {
        uid: user.uid,
        requiresAccountCreation: false,
        defaultPassword: null, // Remove the stored password
        updatedAt: new Date()
      });
      
      // Update tenant assignments with the real UID
      const assignmentsQuery = query(
        collection(db, 'tenantAssignments'),
        where('tenantId', '==', tenantId)
      );
      const assignmentsSnapshot = await getDocs(assignmentsQuery);
      
      for (const assignmentDoc of assignmentsSnapshot.docs) {
        await updateDoc(assignmentDoc.ref, {
          tenantId: user.uid,
          updatedAt: new Date()
        });
      }
      
      // Update properties with the real UID
      const propertiesQuery = query(
        collection(db, 'properties'),
        where('tenantId', '==', tenantId)
      );
      const propertiesSnapshot = await getDocs(propertiesQuery);
      
      for (const propertyDoc of propertiesSnapshot.docs) {
        await updateDoc(propertyDoc.ref, {
          tenantId: user.uid,
          updatedAt: new Date()
        });
      }
      
      // Update profile with display name
      await updateProfile(user, {
        displayName: user.displayName || 'Tenant'
      });
      
      return user;
    } catch (error) {
      throw error;
    }
  },



  // Get tenant assignments by owner
  async getTenantAssignmentsByOwner(ownerId) {
    try {
      const q = query(
        collection(db, 'tenantAssignments'),
        where('ownerId', '==', ownerId)
        // Temporarily removed orderBy to avoid index requirement
      );
      const querySnapshot = await getDocs(q);
      const assignments = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      // Sort assignments client-side for now
      assignments.sort((a, b) => {
        const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt);
        const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt);
        return dateB - dateA; // Descending order
      });
      return assignments;
    } catch (error) {
      throw error;
    }
  },

  // Get tenant assignments by tenant ID
  async getTenantAssignmentsByTenant(tenantId) {
    try {
      const q = query(
        collection(db, 'tenantAssignments'),
        where('tenantId', '==', tenantId),
        where('status', '==', 'active')
      );
      const querySnapshot = await getDocs(q);
      const assignments = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      // Sort assignments client-side for now
      assignments.sort((a, b) => {
        const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt);
        const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt);
        return dateB - dateA; // Descending order
      });
      return assignments;
    } catch (error) {
      throw error;
    }
  },

  // Subscribe to tenant assignments by owner
  subscribeToTenantAssignmentsByOwner(ownerId, callback) {
    try {
      const q = query(
        collection(db, 'tenantAssignments'),
        where('ownerId', '==', ownerId)
        // Temporarily removed orderBy to avoid index requirement
      );

      return onSnapshot(q, (querySnapshot) => {
        const assignments = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        // Sort assignments client-side for now
        assignments.sort((a, b) => {
          const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt);
          const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt);
          return dateB - dateA; // Descending order
        });
        callback(assignments);
      }, (error) => {
        console.error('Error in tenant assignment subscription:', error);
        callback([]);
      });
    } catch (error) {
      console.error('Error setting up tenant assignment subscription:', error);
      callback([]);
      return () => {};
    }
  },

  // Subscribe to tenant assignments by tenant ID
  subscribeToTenantAssignmentsByTenant(tenantId, callback) {
    try {
      const q = query(
        collection(db, 'tenantAssignments'),
        where('tenantId', '==', tenantId),
        where('status', '==', 'active')
      );

      return onSnapshot(q, (querySnapshot) => {
        const assignments = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        // Sort assignments client-side for now
        assignments.sort((a, b) => {
          const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt);
          const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt);
          return dateB - dateA; // Descending order
        });
        callback(assignments);
      }, (error) => {
        console.error('Error in tenant assignment subscription:', error);
        callback([]);
      });
    } catch (error) {
      console.error('Error setting up tenant assignment subscription:', error);
      callback([]);
      return () => {};
    }
  },

  // Subscribe to tenant assignment by property
  subscribeToTenantAssignmentByProperty(propertyId, callback) {
    try {
      const q = query(
        collection(db, 'tenantAssignments'),
        where('propertyId', '==', propertyId),
        where('status', '==', 'active')
      );

      return onSnapshot(q, (querySnapshot) => {
        if (querySnapshot.docs.length > 0) {
          const assignment = {
            id: querySnapshot.docs[0].id,
            ...querySnapshot.docs[0].data()
          };
          callback(assignment);
        } else {
          callback(null);
        }
      }, (error) => {
        console.error('Error in tenant assignment subscription:', error);
        callback(null);
      });
    } catch (error) {
      console.error('Error setting up tenant assignment subscription:', error);
      callback(null);
      return () => {};
    }
  },

  // Get tenant statistics for an owner
  async getTenantStats(ownerId) {
    try {
      const q = query(
        collection(db, 'tenantAssignments'),
        where('ownerId', '==', ownerId)
      );
      const querySnapshot = await getDocs(q);
      const assignments = querySnapshot.docs.map(doc => doc.data());
      
      return {
        totalTenants: assignments.length,
        activeTenants: assignments.filter(assignment => assignment.status === 'active').length,
        terminatedTenants: assignments.filter(assignment => assignment.status === 'terminated').length
      };
    } catch (error) {
      throw error;
    }
  },

  // Remove tenant from property
  async removeTenantFromProperty(propertyId, assignmentId) {
    try {
      // Update property to remove tenant
      const propertyRef = doc(db, 'properties', propertyId);
      await updateDoc(propertyRef, {
        tenantId: null,
        tenantName: null,
        tenantEmail: null,
        leaseStartDate: null,
        leaseEndDate: null,
        status: 'vacant',
        updatedAt: new Date()
      });

      // Update assignment status
      const assignmentRef = doc(db, 'tenantAssignments', assignmentId);
      await updateDoc(assignmentRef, {
        status: 'terminated',
        updatedAt: new Date()
      });
    } catch (error) {
      throw error;
    }
  }
}; 